import { CalendarPicker } from '@components/table/CalendarPicker/CalendarPicker';
import { Popover, PopoverContent, PopoverTrigger } from '@registry/new-york/ui/popover';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';

import { FormError } from '../FormElements/FormElements';
import { Icon } from '../Icon/Icon';

import styles from './DateTimePicker.module.scss';

const fmt = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');

type DateTimePickerProps = {
  control?: any;
  name: string;
  defaultValue?: Date;
  rules?: Object;
  onSelect?: (value: Date) => void;
  className?: string;
  placeholder?: string;
  dateValue?: Date;
  label?: string;
  showTimePicker?: boolean;
  id?: any;
  isClearable?: boolean;
};

const DateInput = ({
  value,
  placeholder,
  onClear,
  isClearable,
}: {
  value?: Date;
  placeholder?: string;
  onClear?: () => void;
  isClearable?: boolean;
}) => (
  <div className={styles.customInput}>
    <Icon name="calendar" />
    <input
      readOnly
      value={value ? fmt(value) : ''}
      placeholder={placeholder}
      className={styles.input}
    />
    {isClearable && value && (
      <button
        type="button"
        className={styles.clearBtn}
        onClick={e => {
          e.stopPropagation();
          onClear?.();
        }}
      >
        ×
      </button>
    )}
  </div>
);

const PickerPopover = ({
  value,
  onChange,
  placeholder,
  isClearable,
}: {
  value?: Date;
  onChange: (d: Date | undefined) => void;
  placeholder?: string;
  isClearable?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <div className={styles.trigger}>
          <DateInput
            value={value}
            placeholder={placeholder}
            isClearable={isClearable}
            onClear={() => onChange(undefined)}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={styles.popover}
        align="start"
        sideOffset={6}
      >
        <CalendarPicker
          mode="single"
          value={value}
          onChange={d => {
            onChange(d);
            setOpen(false);
          }}
          monthsShown={1}
        />
      </PopoverContent>
    </Popover>
  );
};

export const CustomDateTimePicker: React.FC<DateTimePickerProps> = ({
  name,
  control,
  defaultValue,
  className,
  label,
  placeholder,
  rules,
  dateValue,
  onSelect,
  showTimePicker = false,
  id,
  isClearable = false,
}: DateTimePickerProps) =>
  control ? (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ fieldState: { error }, field: { onChange, value } }) => (
        <div
          className={clsx(styles.datePicker, className, { [styles.error]: error })}
          id={id}
        >
          {label && <div className={styles.label}>{label}</div>}
          <PickerPopover
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            isClearable={isClearable}
          />
          {error && (
            <FormError
              className={styles.errorMessage}
              error={error.message}
            />
          )}
        </div>
      )}
    />
  ) : (
    <div className={clsx(styles.datePicker, className)}>
      {label && <div className={styles.label}>{label}</div>}
      <PickerPopover
        value={dateValue}
        onChange={d => d && onSelect?.(d)}
        placeholder={placeholder}
        isClearable={isClearable}
      />
    </div>
  );
